/*
 * **Einhängen per Ziehen** (#204, CONTEXT.md „Einhängen“): Ein Ziel wird erst scharf,
 * wenn der Zeiger auf der MITTE einer Zeile ruht. Rand und Vorbeiziehen bleiben
 * Umsortieren — die Sorge, dass Einhängen dem Sortieren in die Quere kommt.
 */

/** So lange muss der Zeiger auf der Mitte ruhen. (Bis 27.09.2026: 400 ms — fühlte sich „seeehr lang“ an.) */
export const NEST_DWELL_MS = 250
/** Wandert der Zeiger weiter als das, beginnt das Verweilen von vorn. (24 px probiert, Sven blieb bei 12 — 27.09.2026.) */
export const NEST_STILL_PX = 12

export interface Point { x: number; y: number }
export interface RowRect { id: string; top: number; bottom: number; left: number; right: number }

/** Das Ziel, das die Liste zeigt. `blocked`: Es wäre eins, aber Ziel und Gezogene wiederholen sich beide (#205). */
export interface NestTarget { id: string; blocked: boolean }

/** Die Zeile unter dem Zeiger; `middle`, wenn er in ihrer mittleren Hälfte steht. */
export function findNestCandidate(pointer: Point, rows: readonly RowRect[]): { id: string; middle: boolean } | null {
  const row = rows.find(r => pointer.y >= r.top && pointer.y <= r.bottom && pointer.x >= r.left && pointer.x <= r.right)
  if (!row) return null
  const quarter = (row.bottom - row.top) / 4
  return { id: row.id, middle: pointer.y >= row.top + quarter && pointer.y <= row.bottom - quarter }
}

export interface NestTracker {
  /** Bei jeder Zeigerbewegung. */
  update(pointer: Point): void
  /** Ende des Ziehens. */
  reset(): void
  readonly current: NestTarget | null
}

/**
 * Das Verweilen als kleine Maschine: Kandidat → nach `dwellMs` scharf → beim Verlassen der
 * Mitte wieder aus. `candidateAt` misst neu, wenn der Wecker klingelt: Unter einem ruhenden
 * Zeiger können Zeilen weggerückt sein.
 */
export function createNestTracker({
  candidateAt,
  blocked,
  onChange,
  dwellMs = NEST_DWELL_MS,
}: {
  candidateAt: (pointer: Point) => string | null
  /** Ob dieses Ziel die Gezogenen nicht nehmen darf. */
  blocked: (targetId: string) => boolean
  onChange: (target: NestTarget | null) => void
  dwellMs?: number
}): NestTracker {
  let candidate: string | null = null
  let anchor: Point | null = null
  let last: Point | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let target: NestTarget | null = null

  const setTarget = (next: NestTarget | null) => {
    if (target?.id === next?.id && target?.blocked === next?.blocked) return
    target = next
    onChange(next)
  }
  const clearTimer = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }
  const start = (id: string | null, pointer: Point) => {
    clearTimer()
    candidate = id
    anchor = pointer
    if (id) timer = setTimeout(fire, dwellMs)
  }
  const fire = () => {
    timer = null
    if (!last) return
    const id = candidateAt(last)
    if (id && id === candidate) setTarget({ id, blocked: blocked(id) })
    else start(id, last)
  }

  return {
    update(pointer) {
      last = pointer
      const id = candidateAt(pointer)
      if (target) {
        if (id === target.id) return
        setTarget(null)
      }
      if (id !== candidate) return start(id, pointer)
      if (id && anchor && Math.hypot(pointer.x - anchor.x, pointer.y - anchor.y) > NEST_STILL_PX) start(id, pointer)
    },
    reset() {
      clearTimer()
      candidate = null
      anchor = null
      last = null
      setTarget(null)
    },
    get current() {
      return target
    },
  }
}
